'use client';

import React, { useEffect, useRef } from 'react';
import { ObfuscatedEmail } from './ObfuscatedEmail';

/**
 * Client-side component that processes HTML content and replaces
 * email placeholders with ObfuscatedEmail components
 * 
 * This is useful for CMS content that comes as HTML with email placeholders
 * 
 * @example
 * // In your CMS, replace emails with:
 * // <span data-obfuscated-email="sales@active710.com"></span>
 * 
 * // Then use this component:
 * <ObfuscatedEmailClient html={cmsContent} />
 */
interface ObfuscatedEmailClientProps {
    /**
     * HTML content with email placeholders
     * Format: <span data-obfuscated-email="email@example.com" data-display="optional display text"></span>
     */
    html: string;
    
    /**
     * Optional className for the wrapper div
     */
    className?: string;
}

export const ObfuscatedEmailClient: React.FC<ObfuscatedEmailClientProps> = ({ 
    html, 
    className 
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Find all email placeholders
        const emailPlaceholders = containerRef.current.querySelectorAll('[data-obfuscated-email]');

        emailPlaceholders.forEach((placeholder) => {
            const email = placeholder.getAttribute('data-obfuscated-email');
            const displayText = placeholder.getAttribute('data-display');
            const classList = placeholder.getAttribute('class');

            if (!email) return;

            // Create a wrapper div for React portal
            const wrapper = document.createElement('span');
            placeholder.parentNode?.replaceChild(wrapper, placeholder);

            // Render ObfuscatedEmail component
            import('react-dom/client').then(({ createRoot }) => {
                const root = createRoot(wrapper);
                root.render(
                    <ObfuscatedEmail 
                        email={email} 
                        className={classList || undefined}
                    >
                        {displayText || undefined}
                    </ObfuscatedEmail>
                );
            });
        });
    }, [html]);

    return (
        <div 
            ref={containerRef}
            className={className}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export default ObfuscatedEmailClient;

